import styled from 'styled-components'
import { staticColors } from '@/features/shared/theme/staticColors'
import { TextButton } from '@shipt/design-system-buttons'
import { sanitizeHTML } from '@/features/shared/utils/sanitize'
import { Column } from '@shipt/design-system-layouts'
import { CloseIcon } from '@shipt/design-system-icons'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { type JSX } from 'react'
import { Body } from '@shipt/design-system-typography'

type BannerStates = 'alert' | 'information' | 'information_secondary' | 'attention' | 'success'

export type BannerProps = {
  /** Controls the banner colors*/
  type: BannerStates
  /** An optional banner title, when absent, the message text size scales up per typography style guide.*/
  title?: string
  /** Banner message text */
  message: string
  /** Icon to render in the banner */
  icon?: typeof CloseIcon
  /** Please do not use! Used internally for extending styled-components */
  className?: string
  /** Pass a string here to render in a button at end of banner*/
  buttonLabel?: string
  /** Callback function to control button onClick*/
  buttonAction?: (event: React.MouseEvent) => void
  tabIndex?: number
  /** Use with caution: https://reactjs.org/docs/dom-elements.html#dangerouslysetinnerhtml */
  dangerouslySetInnerHTML?: boolean
  /** Making the banner dismissable*/
  dismissable?: boolean
  /** Callback function to control when banner is dismissed*/
  onDismissAction?: (event: React.MouseEvent) => void
  role?: JSX.IntrinsicElements['div']['role']
}

const bannerColors = {
  alert: {
    background: staticColors.red100,
    icon: staticColors.orange700,
  },
  attention: {
    background: staticColors.yellow100,
    icon: staticColors.orange700,
  },
  information: {
    background: staticColors.blue100,
    icon: staticColors.blue500,
  },
  success: {
    background: staticColors.green100,
    icon: staticColors.gray900,
  },
  information_secondary: {
    background: staticColors.oatmeal,
    icon: staticColors.gray800,
  },
}

export const Banner = ({
  title = '',
  type,
  message,
  className,
  icon: Icon,
  buttonLabel,
  buttonAction,
  tabIndex = 0,
  dangerouslySetInnerHTML,
  dismissable,
  onDismissAction,
  ...restProps
}: BannerProps) => {
  const messageSize = title ? 'md' : 'lg'
  const hasButton = Boolean(buttonLabel)

  const handleDismiss = (e: React.MouseEvent) => {
    onDismissAction?.(e)
  }

  return (
    <BaseBanner
      $type={type}
      className={className}
      tabIndex={tabIndex}
      aria-live={type === 'alert' ? 'assertive' : 'off'}
      {...restProps}
    >
      {!!Icon && <Icon />}
      <Column spacing="xxs">
        <div>
          {!!title && (
            <Body size="xl" strong>
              {title}
            </Body>
          )}
          {dangerouslySetInnerHTML ? (
            <Body size={messageSize} dangerouslySetInnerHTML={{ __html: sanitizeHTML(message) }} />
          ) : (
            <Body size={messageSize}>{message}</Body>
          )}
        </div>
        {!!hasButton && <BannerTextButton onClick={buttonAction}>{buttonLabel}</BannerTextButton>}
      </Column>
      {!!dismissable && (
        <TransparentButton>
          <CloseIcon onClick={(e) => handleDismiss(e)} />
        </TransparentButton>
      )}
    </BaseBanner>
  )
}

const BannerTextButton = styled(TextButton)`
  flex: 0 0 auto;
  justify-content: flex-start;
`

const BaseBanner = styled.div<{ $type: BannerStates }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  box-sizing: border-box;
  background-color: ${({ $type }) => bannerColors[$type].background};
  color: ${staticColors.gray900};

  div {
    flex: 1 1 auto;
    margin-right: 12px;
  }

  & > svg {
    color: ${({ $type }) => bannerColors[$type].icon};
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    margin-right: 12px;
  }
`
